import { generate } from '../utils/componentUtils';
import { mDom } from '../utils/renderUtils';
import { handleUtagEvent } from '../actions/initialActions';
import { popAllModalTransition } from '../actions/modalActions';
import { openChatWidgetSaga } from '../sagas/chatWidgetSagas';
import { isEscPressed } from '../../../core/js/utils/keyUtils';
import { error, warn } from '../../../core/js/utils/logger';
import { removeHash } from '../../../core/js/utils/routerUtils';
import { CHAT_SETTINGS_BRAND_NAMES } from '@core/js/constants';

import template from 'chat/chat-widget.j2';

// Check for the embedded_svc object every 1 second for a max of 10 seconds
const INTERVAL_DURATION = 1000;
const MAX_TRIES = 10;

function widgetScriptIsReady() {
    if (window.embedded_svc) {
        return true;
    }
    return false;
}

function hasInitHash(store) {
    const state = store.getState();
    const hash = window.location.hash;
    const initHash = _.get(
        state,
        'core.constants.thirdParty.default.salesforce.chat_widget.init_hash'
    );
    return hash === initHash;
}

function launchReactiveChat(store, component, event) {
    if (widgetScriptIsReady()) {
        closeOpenModals(event);
        handleReactiveChatAnalytics(store);
        prepareWidget(store, component.widgetService, component, true);
        store.runSaga(openChatWidgetSaga, component.widgetService);
    }
}

function declineChatInvite(widgetService) {
    widgetService.inviteAPI.inviteButton.rejectInvite();
}

function setDiscountableSubtotalInvite(store, widgetService, key) {
    const state = store.getState();
    const discountSubtotal = _.get(state, 'core.cart.discountableSubtotal', 0);
    setCustomVariable(widgetService, key, discountSubtotal);
}

function closeOpenModals(event) {
    const payload = popAllModalTransition(event, 'openChatWidgetSaga');
    $.event.trigger({
        type: payload.type,
        payload: payload
    });
}

function handleReactiveChatAnalytics(store) {
    const utagLinkData = {
        "event_category": 'PAGE_TYPE',
        "event_action": 'Reactive Chat Click',
        "event_label": ''
    };
    store.dispatch(handleUtagEvent(utagLinkData, 'link'));
}

function getUserProfile(state, profile) {
    if (state.core.loggedIn && _.size(profile) > 1) {
        const userProfile = [{
            "label": "First Name",
            "value": _.get(profile, 'name.first', ''),
            "displayToAgent": true,
            "transcriptFields": ["FirstName__c"]
        }, {
            "label": "Last Name",
            "value": _.get(profile, 'name.last', ''),
            "displayToAgent": true,
            "transcriptFields": ["LastName__c"]
        }, {
            "label": "Email Address",
            "value": _.get(profile, 'emailAddress', ''),
            "displayToAgent": true,
            "transcriptFields": ["Customer_Email__c"]
        }, {
            "label": "Loyalty Member",
            "value": _.get(state, 'core.loyalty', false) || !_.isEmpty(profile.loyalty),
            "displayToAgent": true,
            "transcriptFields": ["LoyaltyMember__c"]
        }];
        return userProfile;
    }
    return null;
}

// Set general parameters needed to initialize widget
function setBaseSettings(state, settings) {
    const isReactiveChat = settings.isReactive;
    const initParams = _.get(
        state,
        'core.constants.thirdParty.default.salesforce.chat_widget.init_params'
    );
    const contentfulChatConfig = _.get(
        state,
        'core.featureConfigs.chat',
        {},
    );

    const proactiveButtonId = _.get(contentfulChatConfig, 'proactiveButtonId');
    const reactiveButtonId = _.get(contentfulChatConfig, 'reactiveButtonId');
    const proactiveEswConfigDevName = _.get(initParams, 'proactiveEswConfigDevName');
    const reactiveEswConfigDevName = _.get(initParams, 'reactiveEswConfigDevName');
    const proactiveEswLiveAgentDevName = _.get(initParams, 'settings.proactiveEswLiveAgentDevName');
    const reactiveEswLiveAgentDevName = _.get(initParams, 'settings.reactiveEswLiveAgentDevName');

    const defaultSettings = {
        buttonId: isReactiveChat ? reactiveButtonId : proactiveButtonId,
        eswConfigDevName: isReactiveChat ? reactiveEswConfigDevName : proactiveEswConfigDevName,
        eswLiveAgentDevName: isReactiveChat ? reactiveEswLiveAgentDevName : proactiveEswLiveAgentDevName,
        language: '',
        enabledFeatures: ['LiveAgent'],
        entryFeature: 'LiveAgent',
        displayHelpButton: false
    };
    _.assign(settings, defaultSettings);
}

// Set extra details for agent and/or URBN to reference re: the session
function setPrechatFormDetails(state, settings) {
    const profile = _.get(state, 'core.profile');
    const userProfile = getUserProfile(state, profile);
    const brandName = _.get(state, 'core.siteConfig.brand', '').toUpperCase();
    const originValue = _.get(settings, 'isReactive', false) ? "Live Agent" : "Live Agent Proactive";

    const prechatItems = [{
        "label": "Discountable Subtotal",
        "value": _.get(state, 'core.cart.discountableSubtotal', 0),
        "displayToAgent": true,
        "transcriptFields": ["DiscountableSubtotal__c"]
    }, {
        "label": "Cart Count",
        "value": _.get(state, 'core.cart.bagCount', 0),
        "displayToAgent": true,
        "transcriptFields": ["CartCount__c"]
    }, {
        "label": "URBN Tracer",
        "value": _.get(profile, 'tracerValue', null),
        "displayToAgent": true,
        "transcriptFields": ["TracerValue__c"]
    }, {
        "label": "Brand",
        "value": _.get(CHAT_SETTINGS_BRAND_NAMES, brandName, ''),
        "displayToAgent": true
    }, {
        "label": "Site Group",
        "value": _.get(state, 'core.siteConfig.siteGroup', ''),
        "displayToAgent": true
    }, {
        "label": "Site Id",
        "value": _.get(state, 'core.siteId', ''),
        "displayToAgent": true
    }, {
        "label": "origin",
        "value": originValue,
        "displayToAgent": false
    }];

    const concatPrechatItems = userProfile ? prechatItems.concat(userProfile) : prechatItems;
    _.assign(settings, { extraPrechatFormDetails: concatPrechatItems });
}

function setExtraPrechatInfo(state, settings) {
    const extraPrechatInfo = [{
        "entityName": "Account",
        "showOnCreate": true,
        "linkToEntityName": "Case",
        "linkToEntityField": "AccountId",
        "saveToTranscript": "AccountId",
        "entityFieldMaps": [{
            "isExactMatch": true,
            // This does a lookup of the brand so we can add it to the case
            // created in SalesForce, CC_Brand__c here will cause an infinite loop
            "fieldName": "Brand__c",
            "doCreate": false,
            "doFind": true,
            "label": "Brand"
        }, {
            "isExactMatch": true,
            "fieldName": "PersonEmail",
            "doCreate": false,
            "doFind": true,
            "label": "Email"
        }]
    }, {
        "entityName": "Case",
        "showOnCreate": true,
        "saveToTranscript": "CaseId",
        "entityFieldMaps": [{
            "isExactMatch": false,
            "fieldName": "Subject",
            "doCreate": true,
            "doFind": false,
            "label": "issue"
        }, {
            "isExactMatch": false,
            "fieldName": "Status",
            "doCreate": true,
            "doFind": false,
            "label": "Status"
        }, {
            "isExactMatch": false,
            "fieldName": "Origin",
            "doCreate": true,
            "doFind": false,
            "label": "origin"
        }, {
            "isExactMatch": false,
            // This adds the brand as a field to the created case in SalesForce
            // Brand__c here is not a valid brand, must use CC_Brand__c
            "fieldName": "CC_Brand__c",
            "doCreate": true,
            "doFind": false,
            "label": "Brand"
        }]
    }];

    _.assign(settings, { extraPrechatInfo: extraPrechatInfo });
}

function mergeWidgetSettings(store, widgetService, overrides) {
    const state = store.getState();

    setBaseSettings(state, overrides);
    setPrechatFormDetails(state, overrides);
    setExtraPrechatInfo(state, overrides);

    return Object.assign({}, widgetService.settings, overrides);
}

function prepareWidget(store, widgetService, component, isReactive = false) {
    const settingOverrides = component.settingOverrides;
    settingOverrides.isReactive = isReactive || hasInitHash(store);
    const mergedSettings = mergeWidgetSettings(store, widgetService, settingOverrides);
    setDiscountableSubtotalInvite(store, widgetService, 'discountSubtotal');
    // Setting the chat queue will both set the queue and change the proactive button id
    // if a queue specific button exists in Contentful and reactive chat is not active
    setChatQueue(store, widgetService, 'chatQueue', mergedSettings, !isReactive);

    widgetService.settings = mergedSettings;
}

function setChatQueue(store, widgetService, key, mergedSettings, shouldSetProactiveButton = false) {
    const state = store.getState();
    const productBreadcrumbs = _.get(state, 'product.productBreadcrumbs');
    const categoryBreadcrumbs = _.get(state, 'category.breadcrumbs');
    let navItem = {};

    if (productBreadcrumbs) {
        navItem = _.nth(productBreadcrumbs, -2);
    } else if (categoryBreadcrumbs) {
        navItem = _.last(categoryBreadcrumbs);
    }

    const chatQueue = _.get(navItem, 'chatQueue', '');
    // If a chat queue was set and we're not currently reactive, do a lookup
    // on the chat queue name to see if a queue specific proactive button exists
    if (shouldSetProactiveButton) {
        const queueButtons = _.get(
            store.getState(),
            'core.featureConfigs.chat.chatQueueButtonIDs',
            [],
        ).reduce((acc, button) => {
            const { chatQueue: buttonChatQueue, buttonId } = button.fields;
            acc[buttonChatQueue] = buttonId;
            return acc;
        }, {});
        const queueButtonId = queueButtons[chatQueue];
        // Only override the proactive button if we have a valid queue
        if (queueButtonId) {
            mergedSettings.buttonId = queueButtonId;
        }
    }

    setCustomVariable(widgetService, key, chatQueue);
}

function initializeWidget(store, component) {
    const widgetService = component.widgetService;
    prepareWidget(store, widgetService, component);

    const chatConfig = _.get(
        store.getState(),
        'core.constants.thirdParty.default.salesforce.chat_widget.init_params',
        {},
    );
    const contentfulChatConfig = _.get(
        store.getState(),
        'core.featureConfigs.chat',
        {},
    );
    const initParams = Object.assign({}, chatConfig, contentfulChatConfig);

    const baseCoreURL = _.get(initParams, 'baseCoreURL');
    const communityEndpointURL = _.get(initParams, 'communityEndpointURL');
    const orgId = _.get(initParams, 'orgId');
    const eswConfigDevName = _.get(initParams, 'proactiveEswConfigDevName');
    const gslbBaseURL = _.get(initParams, 'gslbBaseURL');
    const baseLiveAgentURL = _.get(initParams, 'baseLiveAgentURL');
    const baseLiveAgentContentURL = _.get(initParams, 'baseLiveAgentContentURL');

    const initConfigs = {
        baseLiveAgentContentURL,
        deploymentId: _.get(initParams, 'settings.deploymentId'),
        baseLiveAgentURL,
        isOfflineSupportEnabled: false,
        avatarImgURL: _.get(initParams, 'avatarImgURL'),
        chatbotAvatarImgURL: _.get(initParams, 'avatarImgURL'),
        smallCompanyLogoImgURL: _.get(initParams, 'avatarImgURL')
    };

    // If any of the values are not present, do not load chat
    const requiredKeys = [
        orgId,
        baseCoreURL,
        baseLiveAgentURL,
        baseLiveAgentContentURL,
        communityEndpointURL,
    ];
    const configIsValid = requiredKeys.every(v => v);
    if (!configIsValid) {
        warn('Unable to load chat, missing required keys', requiredKeys);
        return;
    }

    const widgetShouldOpen = hasInitHash(store);

    /**
     * Initialize third party rendering of chatbot DOM elements
     *
     * @param {string} baseCoreUrl - The base URL for the server where ESW files live.
     * @param {string} communityEndpointUrl - The base URL for the community.
     * @param {string} gslbBaseUrl - The base URL for the global Salesforce load balancer.
     * @param {string} orgId - The entity ID for the organization.
     * @param {string} releaseVersion - The version number for this ESW snippet.
     * @param {string} eswConfigDevName - The developer name for the EmbeddedServiceConfig object.
     * @param {object} settings - A key-value mapping of additional settings provided by features.
     */
    widgetService.init(
        baseCoreURL,
        communityEndpointURL,
        gslbBaseURL,
        orgId,
        eswConfigDevName,
        initConfigs,
    );

    if (widgetShouldOpen) {
        // We need to poll to determine when salesforce chat is ready due to the lack
        // of any sort of ready hook on their end
        let tries = 0;

        const intervalId = setInterval(() => {
            tries++;
            if (tries > MAX_TRIES) {
                error('Chat never initialized, canceling polling');
                clearInterval(intervalId);
                return;
            }
            try {
                store.runSaga(openChatWidgetSaga, widgetService).done.then((success) => {
                    if (success) {
                        clearInterval(intervalId);
                    } else {
                        throw new Error('Chat service not available');
                    }
                });
            } catch (e) {
                warn('Unable to accept chat invite', e);
            }
        }, INTERVAL_DURATION);
    }
}

function shouldSuppressChatModal() {
    const integrationTestSettings = window.__urbnIntegrationTestSettings || {};

    if (_.get(integrationTestSettings, 'suppressChatModal', false)) {
        return true;
    }

    return false;
}

function setCustomVariable(widgetService, key, value) {
    try {
        widgetService.inviteAPI.inviteButton.setCustomVariable(key, value);
    } catch (err) {
        warn(`Unable to set chat invite variable ${key}: `, err);
    }
}

function firstRender(store) {
    if (widgetScriptIsReady() && !shouldSuppressChatModal()) {
        initializeWidget(store, this);
    }
}

function attachEvents(store) {
    $(window).on('hashchange', (event) => {
        const widgetShouldOpen = hasInitHash(store);

        if (widgetShouldOpen) {
            launchReactiveChat(store, this, event);
            removeHash();
        }
    });

    $(document).on('touchend click', '.minimizeButton', (e) => {
        const utagLinkData = {
            "event_category": 'PAGE_TYPE',
            "event_action": 'Minimize Chat',
            "event_label": ''
        };
        store.dispatch(handleUtagEvent(utagLinkData, 'link'));
    });

    $(document).on('touchend click', '.js-accept-chat-invite', (e) => {
        store.runSaga(openChatWidgetSaga, this.widgetService);
    });

    $(document).on('touchend click', '.js-decline-chat-invite', (e) => {
        e.preventDefault();
        declineChatInvite(this.widgetService);
    });

    $(document).on(this.scopedEvent('keyup'), '.js-chat-invitation', (e) => {
        e.preventDefault();
        if (isEscPressed(e)) {
            declineChatInvite(this.widgetService);
        }
    });
}

function onStateChange(store, watchValue) {
    const state = store.getState();
    const isMto = _.get(state, 'mto');

    if (widgetScriptIsReady() && !shouldSuppressChatModal()) {
        if (isMto && (watchValue === 'mto.customize' || 'core.deviceCharacteristics')) {
            const mtoFooterIsVisible = _.get(state.mto, 'customize.showFixedFooter');
            const mtoFixedItemElement = '.js-mto-customize-fixed-item';
            const widgetMinimizeElement = '.embeddedServiceSidebarMinimizedDefaultUI';

            if (mtoFooterIsVisible) {
                $(widgetMinimizeElement).css('bottom', $(mtoFixedItemElement).outerHeight());
            } else {
                $(widgetMinimizeElement).css('bottom', '0');
            }
        } else {
            prepareWidget(store, this.widgetService, this);
        }
    }
}
/**
 * Bootstrap the component onto the page
 * @param {Object} store Redux store
 * @param {Object} overrides Optional component overrides
 * @returns {Object} constructed components
 */
export function init(store, overrides = {}) {
    const config = {
        templateLocation: '.dom-chat-widget',
        watch: ['core.profile', 'mto.customize', 'core.deviceCharacteristics', 'core.cart.discountableSubtotal'],
        name: 'chatWidgetComponent',
        firstRender: firstRender,
        attachEvents: attachEvents,
        onStateChange: onStateChange,
        template: template,
        renderType: mDom,
        settingOverrides: {},
        widgetService: window.embedded_svc
    };
    return generate(config, store, overrides);
}

